/**
 * Learning Center API: the network settings and the run-now buttons (plan
 * docs/plans/lms-plan.md 5.8, 9.1, 9.3, 4.10, section 13 tab 7 Settings,
 * Appendix A "Settings and jobs"; 18.9, 18.26, 18.27; WP-36's routes, WP-48's
 * client; R4, R23). Typed calls over services/api (the shared axios instance
 * that adds the JWT and X-Site-Id); nothing under src/lms imports axios (2.3a).
 * Every route here is an lms admin route (requireLmsAdmin).
 *
 *   GET  /admin/settings           -> the network's lms_settings row (created
 *                                     with the 5.8 defaults on the first read)
 *   PUT  /admin/settings {...}     -> the row after the change; 400 LMS_INVALID
 *                                     names the first bad field, never its value
 *   POST /admin/jobs/run-daily     -> { claimed, summary, tick_key, status?, job_id?, last_job? }
 *   POST /admin/jobs/run-weekly    -> the same for the weekly admin digest
 *
 * The health line (GET /health: notifications_enabled, storage_configured,
 * catalog_pending, the last daily and weekly runs, email_failed_24h) is
 * api/client.ts getLmsHealth (WP-03); the built-in catalog install is
 * api/catalog.ts installCatalog (WP-44). Both are imported from there.
 */
import api from '../../services/api';

// ---------------------------------------------------------------------------
// Shapes (5.8 lms_settings; 18.26)
// ---------------------------------------------------------------------------

/** One network's settings row (5.8). */
export interface LmsSettings {
  id?: string;
  network_id: string;
  /** An IANA time zone name; the hourly tick computes the local day and hour in it (9.1). */
  timezone: string;
  /** due_soon thresholds in days, descending, each 1 to 90 (9.2, 18.27: at least one). */
  reminder_days: number[];
  /** overdue repeats every n days, 1 to 365 (9.2). */
  overdue_repeat_days: number;
  /** A new course's renewal lead, 0 to 365 days (8.3). */
  renewal_lead_days_default: number;
  /** The local hour the daily pass and the digest run from, 0 to 23 (9.1). */
  digest_hour_local: number;
  /** The digest's ISO weekday, 1 (Monday) to 7 (Sunday) (9.3). */
  admin_digest_weekday: number;
  /** Extra digest recipients beyond the lms admins, at most 50 people the admin reaches (9.3, 18.26). */
  admin_digest_user_ids: string[];
  /** Printed on certificates once set (Appendix F, Q6); null prints the organization alone. */
  certificate_signer_name: string | null;
  certificate_signer_title: string | null;
  /** Non-blank, at most 200 characters (18.26). */
  organization_name: string;
  /** A new course's pass mark, 0 to 100 (6.1). */
  default_pass_mark_pct: number;
  /** A new assignment's due window, 1 to 365 days (8.2). */
  default_due_days: number;
  updated_by?: string | null;
  updated_at?: string | null;
}

/** The fields PUT /admin/settings accepts (routes/lms/settings.js SETTINGS_FIELDS). */
export const SETTINGS_FIELDS = [
  'timezone',
  'reminder_days',
  'overdue_repeat_days',
  'renewal_lead_days_default',
  'digest_hour_local',
  'admin_digest_weekday',
  'admin_digest_user_ids',
  'certificate_signer_name',
  'certificate_signer_title',
  'organization_name',
  'default_pass_mark_pct',
  'default_due_days',
] as const;

export type SettingsField = (typeof SETTINGS_FIELDS)[number];

/** A PUT body: only the fields that change. */
export type LmsSettingsPatch = Partial<Pick<LmsSettings, SettingsField>>;

/** The newest job row of a kind, answered when a run-now lost the period's claim (18.26). */
export interface LmsLastJob {
  id: string;
  status: 'running' | 'done' | 'failed' | string;
  started_at: string | null;
  finished_at: string | null;
  summary: Record<string, unknown> | null;
}

/**
 * A run-now answer (routes/lms/jobs.js). claimed true: the pass ran now, with
 * its summary, the job row's status ('done' or 'failed', G10) and id.
 * claimed false: the period's claim was already taken (the scheduled pass or
 * an earlier run), summary null and last_job the newest row of that kind.
 */
export interface LmsJobRunResult {
  claimed: boolean;
  summary: Record<string, unknown> | null;
  tick_key?: string;
  status?: 'running' | 'done' | 'failed' | string;
  job_id?: string;
  last_job?: LmsLastJob | null;
}

export type LmsJobKind = 'daily' | 'weekly';

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** GET /api/lms/admin/settings */
export async function getSettings(): Promise<LmsSettings> {
  const response = await api.get<LmsSettings>('/api/lms/admin/settings');
  return response.data;
}

/** PUT /api/lms/admin/settings with only the changed fields. */
export async function updateSettings(patch: LmsSettingsPatch): Promise<LmsSettings> {
  const response = await api.put<LmsSettings>('/api/lms/admin/settings', patch);
  return response.data;
}

/** POST /api/lms/admin/jobs/run-daily or run-weekly (the body is empty). */
export async function runJobNow(kind: LmsJobKind): Promise<LmsJobRunResult> {
  const path = kind === 'daily' ? '/api/lms/admin/jobs/run-daily' : '/api/lms/admin/jobs/run-weekly';
  const response = await api.post<LmsJobRunResult>(path, {});
  return response.data;
}
